import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Btn, Badge, Card, Spinner, Modal, Select, Input, useToast } from '../components/shared/UI';
import { fmtCurrency, fmtDate, getDocType, getStatus, pricesHidden, balanceDue, DOC_TYPES, STATUS_OPTIONS, PAYABLE_TYPES, PAYMENT_METHODS } from '../utils/helpers';
import { toHtml, hasContent } from '../utils/richText';
import SendShareModal from '../components/documents/SendShareModal';
import RecurringModal from '../components/documents/RecurringModal';
import EInvoiceCard, { isEInvoiceLocked } from '../components/documents/EInvoiceCard';
import { useT } from '../i18n';
import api from '../utils/api';

export default function DocumentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { show, ToastContainer } = useToast();
  const { t } = useT();
  const [doc, setDoc]       = useState(null);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [convertModal, setConvertModal] = useState(false);
  const [convertTo, setConvertTo]       = useState('invoice');
  const [sendModal, setSendModal] = useState(false);
  const [recurModal, setRecurModal] = useState(false);
  const [payModal, setPayModal] = useState(false);
  const [payForm, setPayForm]   = useState({ amount: '', date: '', method: 'bank_transfer', reference: '', note: '' });
  const [paySaving, setPaySaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/documents/${id}`), api.get('/company')])
      .then(([d, c]) => { setDoc(d.data); setCompany(c.data); })
      .finally(() => setLoading(false));
  }, [id]);

  // Payment and status changes return the whole document; keep the populated links we already have
  const applyDoc = data => setDoc(d => ({ ...d, ...data, client: d.client, linkedDoc: d.linkedDoc }));

  const updateStatus = async s => {
    try {
      const { data } = await api.patch(`/documents/${id}/status`, { status: s });
      applyDoc(data);
      show(t('docView.statusUpdated', { s: t(`status.${data.status}`) }));
    } catch (err) { show(err.response?.data?.message || t('editor.errorSaving'), 'error'); }
  };

  const toggleReminders = async e => {
    try {
      const { data } = await api.patch(`/documents/${id}/reminders`, { noReminders: e.target.checked });
      applyDoc(data);
      show(t('reminders.updated'));
    } catch (err) { show(err.response?.data?.message || t('editor.errorSaving'), 'error'); }
  };

  const openPayModal = () => {
    setPayForm({ amount: String(balanceDue(doc)), date: new Date().toISOString().slice(0, 10), method: 'bank_transfer', reference: '', note: '' });
    setPayModal(true);
  };

  const savePayment = async e => {
    e.preventDefault();
    setPaySaving(true);
    try {
      const { data } = await api.post(`/documents/${id}/payments`, { ...payForm, amount: Number(payForm.amount) });
      applyDoc(data);
      setPayModal(false);
      show(t('payments.recorded'));
    } catch (err) { show(err.response?.data?.message || t('payments.failed'), 'error'); }
    finally { setPaySaving(false); }
  };

  const removePayment = async paymentId => {
    if (!window.confirm(t('payments.confirmRemove'))) return;
    try {
      const { data } = await api.delete(`/documents/${id}/payments/${paymentId}`);
      applyDoc(data);
      show(t('payments.removed'));
    } catch (err) { show(err.response?.data?.message || t('payments.failed'), 'error'); }
  };

  const convert = async () => {
    const { data } = await api.post(`/documents/${id}/convert`, { targetType: convertTo });
    show(t('docView.converted', { t: t(`docType.${convertTo}`) }));
    setConvertModal(false);
    navigate(`/documents/${data._id}`);
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/documents/${id}/duplicate`);
      show(t('docView.duplicated'));
      navigate(`/documents/${data._id}/edit`);
    } catch { show(t('docs.duplicateFailed'), 'error'); }
  };

  const openPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/pdf/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch { show(t('editor.pdfFailed'), 'error'); }
    finally { setPdfLoading(false); }
  };

  const downloadWord = async () => {
    try {
      const res = await api.get(`/pdf/${id}/word`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.docNumber}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (err) {
      let msg = t('editor.wordFailed');
      try { const txt = await err.response?.data?.text(); const j = JSON.parse(txt); msg = j.message || msg; } catch {}
      show(msg, 'error');
    }
  };

  const del = async () => {
    if (!window.confirm(t('docView.deleteConfirm'))) return;
    await api.delete(`/documents/${id}`);
    navigate('/documents');
  };

  if (loading) return <Spinner />;
  if (!doc)    return <p style={{ color: '#EF4444', padding: 24 }}>{t('docView.notFound')}</p>;

  const dt  = getDocType(doc.type);
  const st  = getStatus(doc.status);
  const sym = doc.currencySymbol || 'RM';
  const fmt = n => fmtCurrency(n, sym);
  const client = doc.clientSnapshot || {};
  const noPrices = pricesHidden(doc);
  const payable = PAYABLE_TYPES.includes(doc.type);
  const balance = balanceDue(doc);
  const payments = doc.payments || [];

  return (
    <div>
      <ToastContainer />

      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, marginBottom: 8, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
            ← {t('docView.back')}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 28 }}>{dt.icon}</span>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--heading)' }}>{doc.docNumber}</h1>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t(`docType.${doc.type}`)} · {t('docView.issuedOn', { date: fmtDate(doc.issueDate) })}</p>
              {doc.recurring && <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>🔁 {t('recurring.fromSchedule')}</p>}
            </div>
            <Badge label={t(`status.${doc.status}`)} color={st.color} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="primary" onClick={() => setSendModal(true)}>✉ {t('share.send')}</Btn>
          <Btn variant="ghost" onClick={openPdf} disabled={pdfLoading}>
            {pdfLoading ? t('editor.generating') : '🖨 PDF'}
          </Btn>
          <Btn variant="ghost" onClick={downloadWord}>⬇ Word</Btn>
          {!isEInvoiceLocked(doc) && <Btn variant="ghost" onClick={() => navigate(`/documents/${id}/edit`)}>✏️ {t('common.edit')}</Btn>}
          <Btn variant="ghost" onClick={duplicate}>📄 {t('docView.duplicate')}</Btn>
          {doc.type === 'invoice' && <Btn variant="ghost" onClick={() => setRecurModal(true)}>🔁 {t('recurring.make')}</Btn>}
          {(doc.type === 'quotation' || doc.type === 'invoice') && <Btn variant="accent" onClick={() => { setConvertTo(doc.type === 'invoice' ? 'receipt' : 'invoice'); setConvertModal(true); }}>{t('docView.convert')} →</Btn>}
          {!isEInvoiceLocked(doc) && <Btn variant="danger" onClick={del}>{t('common.delete')}</Btn>}
        </div>
      </div>

      {doc.isSample && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
          ✨ {t('onboarding.sampleBanner')}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 20 }}>
        {/* Main doc */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* From / To */}
          <Card>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 24 }}>
              <div>
                <p style={label}>{doc.type === 'bill' ? t('docView.supplier') : t('docView.from')}</p>
                <p style={{ fontWeight: 700, fontSize: 15, color: 'var(--heading)' }}>{company.name || '—'}</p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.7 }}>
                  {company.address}<br />{company.city}<br />{company.phone}<br />{company.email}
                </p>
              </div>
              <div>
                <p style={label}>{t('docView.billedTo')}</p>
                <p style={{ fontWeight: 700, fontSize: 15, color: 'var(--heading)' }}>{client.name || '—'}</p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.7 }}>
                  {client.company && <>{client.company}<br /></>}
                  {client.email && <>{client.email}<br /></>}
                  {client.phone && <>{client.phone}<br /></>}
                  {client.address && <>{client.address}<br /></>}
                  {client.city}
                </p>
              </div>
            </div>
          </Card>

          {/* Items table */}
          <Card style={{ padding: 0 }}>
            {doc.title && (
              <div style={{ padding: '16px 20px 0', fontSize: 16, fontWeight: 700, color: 'var(--heading)' }}>
                {doc.title}
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: noPrices ? 320 : 540, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1A3C6E' }}>
                  <th style={{ ...th, color: '#fff', textAlign: 'left' }}>{t('doc.description')}</th>
                  <th style={{ ...th, color: '#fff', textAlign: 'center', width: 80 }}>{t('doc.qty')}</th>
                  {!noPrices && <>
                    <th style={{ ...th, color: '#fff', textAlign: 'right', width: 140 }}>{t('doc.unitPrice')}</th>
                    <th style={{ ...th, color: '#fff', textAlign: 'right', width: 140 }}>{t('doc.amount')}</th>
                  </>}
                </tr>
              </thead>
              <tbody>
                {doc.items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-light)', background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--hover-bg)' }}>
                    <td style={td} dangerouslySetInnerHTML={{ __html: toHtml(item.description) }} />
                    <td style={{ ...td, textAlign: 'center' }}>{item.quantity}</td>
                    {!noPrices && <>
                      <td style={{ ...td, textAlign: 'right' }}>{fmt(item.unitPrice)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(item.amount)}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {/* Totals */}
            {noPrices ? (
              <div style={{ padding: '14px 20px', fontSize: 12, color: 'var(--text-subtle)' }}>
                {t('docView.pricesHiddenView')}
              </div>
            ) : (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 20px' }}>
              <div style={{ width: '100%', maxWidth: 260 }}>
                {[
                  { l: t('doc.subtotal'), v: doc.subtotal },
                  doc.discountAmount > 0 && { l: `${t('doc.discount')}${doc.discountType === 'percent' ? ` (${doc.discountValue}%)` : ''}`, v: -doc.discountAmount },
                  doc.taxRate > 0 && { l: `${t('doc.tax')} (${doc.taxRate}%)`, v: doc.taxAmount },
                ].filter(Boolean).map(r => (
                  <div key={r.l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', padding: '5px 0', borderBottom: '1px solid var(--border-light)' }}>
                    <span>{r.l}</span><span>{fmt(r.v)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 17, fontWeight: 700, color: 'var(--heading)', paddingTop: 10, marginTop: 4 }}>
                  <span>{t('doc.total')}</span><span>{fmt(doc.total)}</span>
                </div>
                {payable && doc.amountPaid > 0 && balance > 0 && <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', padding: '8px 0 5px' }}>
                    <span>{t('payments.paid')}</span><span>-{fmt(doc.amountPaid)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: 'var(--danger)', borderTop: '1px solid var(--border-light)', paddingTop: 8 }}>
                    <span>{t('payments.balance')}</span><span>{fmt(balance)}</span>
                  </div>
                </>}
              </div>
            </div>
            )}
          </Card>

          {/* Notes & Terms */}
          {(hasContent(doc.notes) || hasContent(doc.terms)) && (
            <Card>
              <div style={{ display: 'grid', gridTemplateColumns: hasContent(doc.notes) && hasContent(doc.terms) ? 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))' : '1fr', gap: 24 }}>
                {hasContent(doc.notes) && <div>
                  <p style={label}>{t('docView.notes')}</p>
                  <div style={richText} dangerouslySetInnerHTML={{ __html: toHtml(doc.notes) }} />
                </div>}
                {hasContent(doc.terms) && <div>
                  <p style={label}>{t('docView.terms')}</p>
                  <div style={richText} dangerouslySetInnerHTML={{ __html: toHtml(doc.terms) }} />
                </div>}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Status */}
          <Card>
            <p style={label}>{t('docView.updateStatus')}</p>
            <Select value={doc.status} onChange={e => updateStatus(e.target.value)}>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{t(`status.${s.value}`)}</option>)}
            </Select>
          </Card>

          <EInvoiceCard doc={doc} onDocChange={applyDoc} show={show} />

          {/* Sharing: link state, client views, last email, quotation answer */}
          <Card>
            <p style={label}>{t('share.cardTitle')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              <div style={{ color: doc.shareToken ? 'var(--success)' : 'var(--text-subtle)', fontWeight: 600 }}>
                {doc.shareToken ? `● ${t('share.linkOn')}` : t('share.linkOff')}
              </div>
              {doc.shareToken && <div>
                {doc.shareViews > 0 ? `👁 ${t('share.views', { n: doc.shareViews })}` : t('share.notViewed')}
                {doc.lastViewedAt && <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{t('share.lastViewed', { date: new Date(doc.lastViewedAt).toLocaleString() })}</div>}
              </div>}
              {doc.emailLog?.length > 0 && (() => {
                const last = doc.emailLog[doc.emailLog.length - 1];
                return <div style={{ overflowWrap: 'anywhere' }}>✉ {t('share.lastEmailed', { to: last.to, date: fmtDate(last.at) })}</div>;
              })()}
              {doc.clientResponse?.action && (
                <div style={{ fontWeight: 600, color: doc.clientResponse.action === 'accepted' ? 'var(--success)' : 'var(--danger)' }}>
                  {t(`share.responded_${doc.clientResponse.action}`, { name: doc.clientResponse.name, date: fmtDate(doc.clientResponse.at) })}
                </div>
              )}
            </div>
            <Btn size="sm" variant="outline" onClick={() => setSendModal(true)} style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}>✉ {t('share.send')}</Btn>
          </Card>

          {/* Payments */}
          {payable && !noPrices && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <p style={{ ...label, marginBottom: 0 }}>{t('payments.title')}</p>
                {balance > 0 && doc.status !== 'cancelled' && (
                  <Btn size="sm" variant="accent" onClick={openPayModal}>+ {t('payments.record')}</Btn>
                )}
              </div>
              {payments.length === 0
                ? <p style={{ fontSize: 13, color: 'var(--text-subtle)' }}>{doc.status === 'paid' ? t('payments.fullyPaid') : t('payments.none')}</p>
                : payments.map(p => (
                  <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{fmt(p.amount)}</div>
                      <div style={{ color: 'var(--text-subtle)', fontSize: 12, overflowWrap: 'anywhere' }}>
                        {fmtDate(p.date)} · {t(`payments.m_${p.method}`)}{p.reference ? ` · ${p.reference}` : ''}{p.note ? ` · ${p.note}` : ''}
                      </div>
                    </div>
                    <button onClick={() => removePayment(p._id)} title={t('common.delete')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subtle)', fontSize: 16, padding: 0, alignSelf: 'flex-start' }}>×</button>
                  </div>
                ))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                <Row label={t('payments.paid')} val={fmt(doc.status === 'paid' ? doc.total : doc.amountPaid || 0)} />
                <Row label={t('payments.balance')} val={fmt(balance)} />
              </div>
              {doc.type === 'invoice' && balance > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-light)', fontSize: 12.5, color: 'var(--text-muted)' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!doc.noReminders} onChange={toggleReminders} style={{ marginTop: 2 }} />
                    {t('reminders.docToggle')}
                  </label>
                  {doc.remindersSent?.length > 0 && <div style={{ marginTop: 6, color: 'var(--text-subtle)' }}>⏰ {t('reminders.docSent', { n: doc.remindersSent.length })}</div>}
                </div>
              )}
            </Card>
          )}

          {/* Dates */}
          <Card>
            <p style={label}>{t('docView.dates')}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Row label={t('editor.issueDate')} val={fmtDate(doc.issueDate)} />
              {doc.dueDate    && <Row label={t('editor.dueDate')}    val={fmtDate(doc.dueDate)} />}
              {doc.validUntil && <Row label={t('editor.validUntil')} val={fmtDate(doc.validUntil)} />}
            </div>
          </Card>

          {/* Linked doc */}
          {doc.linkedDoc && (
            <Card>
              <p style={label}>{t('docView.linkedDocument')}</p>
              <button onClick={() => navigate(`/documents/${doc.linkedDoc._id}`)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontWeight: 600, fontSize: 13, fontFamily: 'inherit', padding: 0 }}>
                {doc.linkedDoc.docNumber} ({t(`docType.${doc.linkedDoc.type}`)})
              </button>
            </Card>
          )}

          {/* Bank details */}
          {company.bankName && (
            <Card style={{ background: 'rgba(46,117,182,0.08)', border: '1px solid rgba(46,117,182,0.3)' }}>
              <p style={{ ...label, color: 'var(--blue)' }}>{t('docView.paymentDetails')}</p>
              <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.8 }}>
                <div><b>{t('docView.bank')}:</b> {company.bankName}</div>
                <div><b>{t('docView.account')}:</b> {company.bankAccount}</div>
                {company.bankHolder && <div><b>{t('docView.name')}:</b> {company.bankHolder}</div>}
              </div>
            </Card>
          )}
        </div>
      </div>

      <RecurringModal open={recurModal} onClose={() => setRecurModal(false)} doc={doc} show={show} />
      <SendShareModal open={sendModal} onClose={() => setSendModal(false)} doc={doc} company={company} onDocChange={applyDoc} show={show} />

      {/* Record payment modal */}
      <Modal open={payModal} onClose={() => setPayModal(false)} title={`${t('payments.record')} — ${doc.docNumber}`} width={460}>
        <form onSubmit={savePayment} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('payments.balance')}: <b style={{ color: 'var(--text)' }}>{fmt(balance)}</b></p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label={`${t('payments.amount')} (${sym})`} type="number" step="0.01" min="0.01" max={balance} required autoFocus
              value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} />
            <Input label={t('payments.date')} type="date" required
              value={payForm.date} onChange={e => setPayForm(f => ({ ...f, date: e.target.value }))} />
          </div>
          <Select label={t('payments.method')} value={payForm.method} onChange={e => setPayForm(f => ({ ...f, method: e.target.value }))}>
            {PAYMENT_METHODS.map(m => <option key={m} value={m}>{t(`payments.m_${m}`)}</option>)}
          </Select>
          <Input label={t('payments.reference')} placeholder={t('payments.referencePh')} maxLength={120}
            value={payForm.reference} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} />
          <Input label={t('payments.note')} placeholder={t('payments.notePh')} maxLength={500}
            value={payForm.note} onChange={e => setPayForm(f => ({ ...f, note: e.target.value }))} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" type="button" onClick={() => setPayModal(false)}>{t('common.cancel')}</Btn>
            <Btn variant="primary" type="submit" disabled={paySaving}>{paySaving ? t('common.saving') : t('payments.save')}</Btn>
          </div>
        </form>
      </Modal>

      {/* Convert modal */}
      <Modal open={convertModal} onClose={() => setConvertModal(false)} title={t('docView.convertDocument')}>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
          {t('docView.convertIntro', { n: doc.docNumber })}
        </p>
        <Select label={t('docView.convertTo')} value={convertTo} onChange={e => setConvertTo(e.target.value)}>
          {DOC_TYPES
            .filter(x => x.value !== doc.type)
            .filter(x => doc.type === 'invoice' ? x.value === 'receipt' : x.value !== 'quotation')
            .map(x => <option key={x.value} value={x.value}>{x.icon} {t(`docType.${x.value}`)}</option>)
          }
        </Select>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <Btn variant="ghost" onClick={() => setConvertModal(false)}>{t('common.cancel')}</Btn>
          <Btn variant="primary" onClick={convert}>{t('docView.convert')}</Btn>
        </div>
      </Modal>
    </div>
  );
}

function Row({ label: l, val }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
      <span style={{ color: 'var(--text-subtle)' }}>{l}</span>
      <span style={{ fontWeight: 500, color: 'var(--text)' }}>{val}</span>
    </div>
  );
}

const label = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-subtle)', marginBottom: 8 };
// Rich-text blocks bring their own <p>/<ul> markup, so they need list padding
// back — the global reset zeroes it.
const richText = { fontSize: 13, color: 'var(--text)', lineHeight: 1.7, paddingLeft: 0 };
const th    = { padding: '12px 16px', fontSize: 12, fontWeight: 600 };
const td    = { padding: '12px 16px', fontSize: 13, color: 'var(--text)' };
